import React, { useId } from "react";

import { Icon, IconSize } from "@gaming/xds-react-js";
import { Info } from "@gaming/xds-react-js-icons";

import * as styles from "../XboxOnboardPage.module.css";

type FormFieldProps = Readonly<{
  label: string;
  value: string;
  onChange: (value: string) => void;
  required?: boolean;
  disabled?: boolean;
  type?: string;
  maxLength?: number;
  error?: string;
  subtitle?: string;
  tooltip?: string;
}>;

export const FormField = ({
  label,
  value,
  onChange,
  required,
  disabled,
  type,
  maxLength,
  error,
  subtitle,
  tooltip,
}: FormFieldProps) => {
  const fieldId = useId();
  const errorId = `${fieldId}-error`;
  const hintId = `${fieldId}-hint`;
  const tooltipId = `${fieldId}-tooltip`;
  const describedBy = [
    error ? errorId : undefined,
    subtitle ? hintId : undefined,
    tooltip ? tooltipId : undefined,
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <div className={styles.formGroup}>
      <div className={styles.formLabelRow}>
        <label className={styles.formLabel} htmlFor={fieldId}>
          {label}
          {required && (
            <span className={styles.requiredMark} aria-hidden="true">
              *
            </span>
          )}
        </label>
        {tooltip && (
          <span className={styles.fieldTooltip}>
            <button
              type="button"
              className={styles.fieldTooltipButton}
              aria-label={tooltip}
              aria-describedby={tooltipId}
            >
              <Icon aria-hidden="true" icon={Info} iconSize={IconSize.Custom} />
            </button>
            <span id={tooltipId} className={styles.fieldTooltipCard} role="tooltip">
              {tooltip}
            </span>
          </span>
        )}
      </div>
      <input
        id={fieldId}
        type={type ?? "text"}
        className={styles.formInput}
        value={value}
        onChange={e => onChange(e.target.value)}
        disabled={disabled}
        maxLength={maxLength}
        required={required}
        aria-required={required}
        aria-invalid={error ? true : undefined}
        aria-describedby={describedBy || undefined}
      />
      {error && (
        <span id={errorId} className={styles.validationError} role="alert">
          {error}
        </span>
      )}
      {subtitle && (
        <p id={hintId} className={styles.fieldHint}>
          {subtitle}
        </p>
      )}
    </div>
  );
};
